import { Settings, StreamrWrapper, } from '@streamr/tv-engine';
import { scheduleAtInterval, toUserId, } from '@streamr/utils';
import { Mutex } from 'async-mutex';
import { EventEmitter } from 'eventemitter3';
import { MediaProducer } from '../engine/mediaproducer/MediaProducer';
// TODO: StreamrTvBroadcaster should not know about any services
// StreamrTvBroadcaster is a library that the UI services use, not the other way around
import { PluginApiService } from '../services/pluginapi/PluginApiService';
import { PurchaseService } from '../services/purchase/PurchaseService';
import { ViewerCounter } from './helpers/ViewerCounter';
export class StreamrTvBroadcaster extends EventEmitter {
    streamrWrapper;
    mediaProducer;
    viewerCounter;
    mutex;
    broadcastMetadataAbortController;
    streamId;
    myPrivateKey;
    aesKey;
    constructor(opts) {
        super();
        this.streamId = opts.streamId;
        this.myPrivateKey = opts.myPrivateKey;
        this.aesKey = opts.aesKey;
        this.mediaProducer = new MediaProducer();
        this.viewerCounter = new ViewerCounter();
        this.mutex = new Mutex();
        this.mediaProducer.on('mediaPacketFragment', this.onMediaPacketFragment);
        this.mediaProducer.on('audioVolume', this.onAudioVolume);
        this.viewerCounter.on('countChanged', this.onCountChanged);
    }
    async start() {
        await this.mutex.acquire();
        try {
            this.streamrWrapper = new StreamrWrapper({
                aesKey: this.aesKey,
                myPrivateKey: this.myPrivateKey,
            });
            const pluginApiService = PluginApiService.getInstance();
            pluginApiService.setStreamrWrapper(this.streamrWrapper);
            const purchaseService = PurchaseService.getInstance();
            purchaseService.setStreamrWrapper(this.streamrWrapper);
            this.streamrWrapper.on('heartbeat', this.onHeartbeat);
            this.streamrWrapper.on('leave', this.onLeave);
            this.streamrWrapper.on('purchaseClaimedMessage', this.onPurchaseClaimedMessage);
            await this.streamrWrapper.start(this.streamId);
            this.viewerCounter.start();
            this.setupBroadcastMetadataInterval();
        }
        finally {
            this.mutex.release();
        }
    }
    async stop() {
        await this.mutex.acquire();
        try {
            this.clearBroadcastMetadataInterval();
            this.streamrWrapper?.off('heartbeat', this.onHeartbeat);
            this.streamrWrapper?.off('leave', this.onLeave);
            try {
                this.streamrWrapper?.publishLeave();
            }
            catch (error) {
                console.error('Error publishing leave during stop', error);
            }
            await this.streamrWrapper?.stop();
            this.viewerCounter.stop();
            await this.mediaProducer.stop();
        }
        finally {
            this.mutex.release();
        }
    }
    async startMediaProducer(mediaStream) {
        await this.mutex.acquire();
        try {
            await this.mediaProducer.start(mediaStream);
        }
        finally {
            this.mutex.release();
        }
    }
    async generateThumbnail() {
        return await this.mediaProducer.generateThumbnail();
    }
    sendLeave() {
        try {
            this.streamrWrapper?.publishLeave();
        }
        finally {
            this.mutex.release();
        }
    }
    async stopMediaProducer() {
        await this.mutex.acquire();
        try {
            await this.mediaProducer.stop();
        }
        finally {
            this.mutex.release();
        }
    }
    setVideoEncodingQuality(videoEncodingQuality) {
        this.mediaProducer.setVideoEncodingQuality(videoEncodingQuality);
    }
    getVideoEncodingQuality() {
        return this.mediaProducer.getVideoEncodingQuality();
    }
    clearBroadcastMetadataInterval() {
        this.broadcastMetadataAbortController?.abort();
        this.broadcastMetadataAbortController = undefined;
    }
    setupBroadcastMetadataInterval() {
        this.broadcastMetadataAbortController = new AbortController();
        scheduleAtInterval(async () => {
            this.streamrWrapper?.publishBroadcastMetadata({
                viewerCount: this.viewerCounter.getCount(),
                products: PurchaseService.getInstance().getProductsOfActiveBroadcast(),
            });
        }, Settings.broadcastMetadataInterval, true, this.broadcastMetadataAbortController.signal);
    }
    onMediaPacketFragment = (mediaPacketFragment) => {
        this.streamrWrapper?.publishMediaPacketFragment(mediaPacketFragment);
    };
    onAudioVolume = (volume) => {
        this.emit('audioVolume', volume);
    };
    onCountChanged = (count) => {
        this.emit('viewerCountChanged', count);
    };
    onHeartbeat = (heartbeat, publisherId) => {
        if (heartbeat.isRecording) {
            this.emit('heartbeatFromRecorder');
        }
        this.viewerCounter.handleHeartbeat(heartbeat, toUserId(publisherId));
    };
    onLeave = (leave, publisherId) => {
        this.viewerCounter.handleLeave(leave, toUserId(publisherId));
    };
    onPurchaseClaimedMessage = (message, publisherId) => {
        PurchaseService.getInstance().onPurchaseClaimedMessage(message, toUserId(publisherId));
    };
}
